import { useState, useEffect , useCallback} from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Phone, Mail, Globe, Clock, ArrowLeft, Star, BadgeCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReviewsSection from "@/components/ReviewsSection";
import BusinessExternalReviewsSection from "@/components/BusinessExternalReviewsSection";
import BusinessEditDialog from "@/components/BusinessEditDialog";
import AssignOwnerDialog from "@/components/AssignOwnerDialog";
import ClaimQRDialog from "@/components/ClaimQRDialog";
import FavoriteButton from "@/components/FavoriteButton";
import { trackEvent } from "@/lib/analytics";
import { resolveAssetUrl } from "@/lib/assetUrl";
import RelatedBusinessesRail from "@/components/RelatedBusinessesRail";
import { SOCIAL_PLATFORMS, resolveSocialUrl } from "@/components/SocialLinksInput";
import { useNavigate } from "react-router-dom";
import RealtorProfileLayout from "@/components/RealtorProfileLayout";
import BarberProfileLayout, { isBarberBusiness } from "@/components/BarberProfileLayout";
import StorefrontHero from "@/components/storefront/StorefrontHero";
import ReviewsMiniBar from "@/components/storefront/ReviewsMiniBar";
import OwnerToolbar from "@/components/storefront/OwnerToolbar";
import StorefrontTabs from "@/components/storefront/StorefrontTabs";
import StorefrontProductGrid from "@/components/storefront/StorefrontProductGrid";
import PhotosTabPanel from "@/components/storefront/PhotosTabPanel";
import PickupHoursCard from "@/components/storefront/PickupHoursCard";

// BC Score Stars Component (interactive for rating)
const BCScoreInput = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }, (_, i) => (
        <button
          key={`star-${i}`}
          type="button"
          onClick={() => onChange(i + 1)}
          onMouseEnter={() => setHover(i + 1)}
          onMouseLeave={() => setHover(0)}
          className="p-0.5"
        >
          <Star
            className={`w-6 h-6 transition-colors ${
              (hover || value) > i
                ? "fill-tbc-gold text-tbc-gold"
                : "text-gray-300"
            }`}
          />
        </button>
      ))}
      <span className="ml-2 text-lg font-heading text-tbc-navy">
        {value || hover || 0}/5
      </span>
    </div>
  );
};

export default function BusinessProfilePage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, login } = useAuth();
  const [business, setBusiness] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [newReview, setNewReview] = useState({ score: 0, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewsRefreshKey, setReviewsRefreshKey] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [assignOwnerOpen, setAssignOwnerOpen] = useState(false);
  const [claimQrOpen, setClaimQrOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [activeTab, setActiveTab] = useState("products");

  // Hydrate the "already favorited?" state for the heart in the hero action row.
  useEffect(() => {
    if (!user || !business) {
      setIsFavorited(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/favorites/ids`, { withCredentials: true });
        const ids = new Set(res.data.business_ids || []);
        setIsFavorited(ids.has(business.business_id));
      } catch {
        // Silent — heart defaults to empty.
      }
    })();
  }, [user, business]);

  // Public self-claim — used by the "Is this your business? Claim it" CTA.
  // The DM link we send business owners lands on this page; tapping the CTA
  // routes them through sign-in (if needed) then POSTs to /claims/self-claim.
  // Auto-approves if their signed-in email matches business.email; otherwise
  // creates a pending admin-review claim.
  const handleSelfClaim = async () => {
    if (!business) return;
    if (!user) {
      // Persist the current URL so the auth flow returns them here after login
      try {
        localStorage.setItem(
          "post_login_redirect",
          `/business/${business.business_id}?claim=1`,
        );
      } catch {
        /* localStorage may be blocked — non-fatal */
      }
      toast.info("Sign in first so we can attach the business to your account.");
      login();
      return;
    }
    setClaiming(true);
    try {
      const res = await axios.post(
        `${API}/claims/self-claim/${business.business_id}`,
        {},
        { withCredentials: true },
      );
      const data = res.data || {};
      if (data.status === "approved") {
        toast.success(
          `${data.business_name || "This business"} is now yours — head to your dashboard.`,
          { duration: 6000 },
        );
        setTimeout(() => navigate("/dashboard"), 1200);
      } else {
        toast.success(
          data.message ||
            "Claim submitted for review. We'll email you when it's approved (24-48h).",
          { duration: 8000 },
        );
      }
      fetchBusiness();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit claim.");
    } finally {
      setClaiming(false);
    }
  };

  // Admin-only: hard-delete this business from the profile page. Cascades
  // to products / reviews / featured slots and cancels any active Stripe
  // sub. Same endpoint the owner dashboard uses.
  const handleAdminDelete = async () => {
    if (!business) return;
    const confirmMsg = `Permanently DELETE "${business.name}"?\n\nThis removes the listing, all its products, reviews, featured slots, and cancels any active subscription. This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${API}/businesses/${business.business_id}`, {
        withCredentials: true,
      });
      toast.success(`"${business.name}" deleted.`);
      navigate("/admin?tab=verifications");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const fetchBusiness = useCallback(async () => {
    setLoading(true);
    try {
      const [bizRes, prodRes] = await Promise.all([
        axios.get(`${API}/businesses/${businessId}`),
        axios.get(`${API}/businesses/${businessId}/products`)
      ]);
      setBusiness(bizRes.data);
      setProducts(prodRes.data.products || []);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error fetching business:", error);
      toast.error("Failed to load business");
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchBusiness();
  }, [fetchBusiness]);

  useEffect(() => {
    if (businessId) trackEvent(businessId, "profile_view");
  }, [businessId]);

  // Capture a `?ref=TBC-XXXXXX` code from a shared claim URL and stash it in
  // sessionStorage. If the visitor signs up via the "Claim this business"
  // CTA, `AuthDialog` reads this and credits the referrer's TBC Points.
  useEffect(() => {
    const ref = (searchParams.get("ref") || "").trim();
    if (ref && /^TBC-[A-Z0-9]{6}$/i.test(ref)) {
      try {
        sessionStorage.setItem("tbc.pending_ref", ref.toUpperCase());
      } catch { /* sessionStorage may be blocked */ }
    }
  }, [searchParams]);

  // Auto-trigger the self-claim flow when the URL carries ?claim=1 and the
  // business is unclaimed. This closes the loop for users who came here
  // straight from a DM link and just signed in via the CTA banner.
  useEffect(() => {
    if (!business || !user) return;
    if (searchParams.get("claim") !== "1") return;
    if (business.is_claimed) return;
    if (business.owner_id === user.user_id) return;
    handleSelfClaim();
    // Strip the query so a page refresh doesn't re-fire the claim.
    searchParams.delete("claim");
    setSearchParams(searchParams, { replace: true });
  }, [business, user]);

  // Check admin status once per user session. Fails silently for non-admins.
  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/admin/check`, { withCredentials: true })
      .then((r) => {
        if (!cancelled) setIsAdmin(!!r.data?.is_admin);
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const addToCart = async (productId) => {
    if (!user) {
      login();
      return;
    }

    try {
      await axios.post(
        `${API}/cart/add`,
        { product_id: productId, quantity: 1 },
        { withCredentials: true }
      );
      toast.success("Added to cart!");
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  const submitReview = async () => {
    if (!user) {
      login();
      return;
    }

    if (newReview.score < 1) {
      toast.error("Please select a BC Score");
      return;
    }

    setSubmittingReview(true);
    try {
      await axios.post(
        `${API}/businesses/${businessId}/reviews`,
        newReview,
        { withCredentials: true }
      );
      toast.success("Review submitted!");
      setReviewDialogOpen(false);
      setNewReview({ score: 0, comment: "" });
      setReviewsRefreshKey((k) => k + 1);
      fetchBusiness();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-80 bg-gray-200 rounded-xl"></div>
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="font-heading text-3xl text-tbc-navy mb-4">Business not found</h1>
          <Link to="/directory">
            <Button className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full">
              Back to Directory
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Toggle Google reviews for this listing — shared between the standard
  // profile layout and the RealtorProfileLayout so admins get the same
  // behaviour regardless of which layout is currently rendering.
  const handleToggleGoogleReviews = async () => {
    const disable = !business.google_reviews_disabled;
    const label = disable
      ? "Turn OFF Google reviews for this listing?"
      : "Turn Google reviews back ON?";
    if (!window.confirm(label)) return;
    try {
      await axios.post(
        `${API}/admin/external-reviews/toggle`,
        { business_id: business.business_id, disabled: disable },
        { withCredentials: true },
      );
      setBusiness((prev) => ({ ...prev, google_reviews_disabled: disable }));
      toast.success(disable ? "Google reviews disabled." : "Google reviews re-enabled.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };

  // Category-specific layout routing. Real Estate businesses render the
  // premium RealtorProfileLayout (Julius Scott hero design). Barber shops
  // (matched by category + keywords) render the BarberProfileLayout with
  // price sheet, cuts gallery, and chair-booking indicator. Every other
  // category keeps the standard directory profile layout below.
  const isRealtor = business.category === "Real Estate";
  const isBarber = !isRealtor && isBarberBusiness(business);

  const CategoryLayout = isRealtor
    ? RealtorProfileLayout
    : isBarber
    ? BarberProfileLayout
    : null;

  if (CategoryLayout) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <CategoryLayout
          business={business}
          user={user}
          isAdmin={isAdmin}
          isFavorited={isFavorited}
          setIsFavorited={setIsFavorited}
          onEdit={() => setEditOpen(true)}
          onAssignOwner={() => setAssignOwnerOpen(true)}
          onClaimQR={() => setClaimQrOpen(true)}
          onDelete={handleAdminDelete}
          onToggleGoogleReviews={handleToggleGoogleReviews}
          reviewsRefreshKey={reviewsRefreshKey}
          onReviewSubmitted={() => {
            setReviewsRefreshKey((k) => k + 1);
            fetchBusiness();
          }}
        />
        <RelatedBusinessesRail businessId={business.business_id} />
        <Footer />
        {isAdmin && (
          <BusinessEditDialog
            business={business}
            open={editOpen}
            onOpenChange={setEditOpen}
            onSaved={(updated) => {
              if (updated) setBusiness(updated);
              setEditOpen(false);
            }}
          />
        )}
        {isAdmin && (
          <AssignOwnerDialog
            business={business}
            open={assignOwnerOpen}
            onOpenChange={setAssignOwnerOpen}
            onAssigned={(updated) => {
              if (updated) setBusiness((prev) => ({ ...prev, ...updated }));
            }}
          />
        )}
        {isAdmin && (
          <ClaimQRDialog
            business={business}
            open={claimQrOpen}
            onOpenChange={setClaimQrOpen}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Self-Claim CTA — visible when this listing is unclaimed AND the
          current viewer isn't already its owner or an admin. Powers the
          "share this link with a business owner via DM" workflow. */}
      {!business.is_claimed && !isAdmin && business.owner_id !== user?.user_id && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-tbc-gold/40 bg-tbc-cream/60 px-5 py-4"
            data-testid="claim-cta-banner"
          >
            <div className="flex items-start gap-3 min-w-0">
              <div className="h-9 w-9 rounded-full bg-tbc-gold/20 flex items-center justify-center flex-none">
                <BadgeCheck className="w-5 h-5 text-tbc-gold" />
              </div>
              <div className="min-w-0">
                <p className="font-heading text-tbc-navy text-base sm:text-lg">
                  Is this your business?
                </p>
                <p className="text-sm text-gray-600">
                  Claim <strong>{business.name}</strong> to update photos, hours,
                  products, and respond to reviews. Takes about 30 seconds.
                </p>
              </div>
            </div>
            <Button
              type="button"
              onClick={handleSelfClaim}
              disabled={claiming}
              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-5 flex-none"
              data-testid="self-claim-btn"
            >
              {claiming ? "Submitting…" : user ? "Claim this business" : "Sign in to claim"}
            </Button>
          </div>
        </div>
      )}

      <StorefrontHero
        business={business}
        isFavorited={isFavorited}
        onFavoriteChange={setIsFavorited}
      />

      {isAdmin && (
        <OwnerToolbar
          business={business}
          onEdit={() => setEditOpen(true)}
          onAssignOwner={() => setAssignOwnerOpen(true)}
          onClaimQR={!business.is_claimed ? () => setClaimQrOpen(true) : undefined}
          onDelete={handleAdminDelete}
          onToggleGoogleReviews={handleToggleGoogleReviews}
        />
      )}

      {/* Storefront body — reviews mini-bar, tabs, and tab panels. */}
      <section className="py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ReviewsMiniBar
            score={Number(business.bc_score || 0)}
            count={Number(business.bc_score_count || 0)}
            canReview={!!(user && user.user_id !== business.owner_id)}
            onWriteReview={() => setReviewDialogOpen(true)}
            onSeeReviews={() => setActiveTab("reviews")}
          />
        </div>

        <StorefrontTabs
          business={business}
          active={activeTab}
          onChange={setActiveTab}
          hasPhotos={
            (Array.isArray(business.gallery_images) && business.gallery_images.length > 0) ||
            (!!user && (isAdmin || user.user_id === business.owner_id))
          }
          reviewCount={Number(business.bc_score_count || 0)}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Rate dialog is shared across tabs so any Write-a-review link opens it. */}
          <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Rate {business.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 pt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Your BC Score</label>
                  <BCScoreInput
                    value={newReview.score}
                    onChange={(score) => setNewReview({ ...newReview, score })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Review (Optional)
                  </label>
                  <Textarea
                    value={newReview.comment}
                    onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                    placeholder="Share your experience with this business..."
                    rows={4}
                  />
                </div>
                <Button
                  onClick={submitReview}
                  disabled={submittingReview || newReview.score < 1}
                  className="w-full bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full py-6"
                  data-testid="submit-review-button"
                >
                  {submittingReview ? "Submitting..." : "Submit Review"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Tab panels */}
          {activeTab === "products" && (
            <div role="tabpanel" data-testid="tab-panel-products">
              <StorefrontProductGrid
                business={business}
                products={products}
                onAddToCart={(product) => addToCart(product.product_id)}
              />
            </div>
          )}

          {activeTab === "about" && (
            <div role="tabpanel" data-testid="tab-panel-about" className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h2 className="font-heading text-xl sm:text-2xl text-tbc-navy mb-3">About</h2>
                  <p
                    className="text-gray-600 font-body leading-relaxed whitespace-pre-wrap"
                    data-testid="business-description"
                  >
                    {business.description}
                  </p>
                </div>
              </div>
              <aside className="space-y-4">
                <PickupHoursCard business={business} />
                <div className="bg-tbc-cream rounded-2xl p-5">
                  <h3 className="font-heading text-lg text-tbc-navy mb-3">Contact</h3>
                  <div className="space-y-3">
                    {(business.address || business.city) && (
                      <div className="flex items-start gap-2.5">
                        <MapPin className="w-4 h-4 text-tbc-gold mt-0.5 flex-shrink-0" />
                        <div className="text-gray-600 font-body text-sm">
                          {business.address && <p>{business.address}</p>}
                          {business.city && business.state && (
                            <p>{business.city}, {business.state} {business.zip_code}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {business.phone && (
                      <div className="flex items-center gap-2.5">
                        <Phone className="w-4 h-4 text-tbc-gold flex-shrink-0" />
                        <a
                          href={`tel:${business.phone}`}
                          onClick={() => trackEvent(business.business_id, "contact_click", { metadata: { channel: "phone" } })}
                          className="text-gray-600 font-body text-sm hover:text-tbc-navy"
                        >
                          {business.phone}
                        </a>
                      </div>
                    )}
                    {business.email && (
                      <div className="flex items-center gap-2.5">
                        <Mail className="w-4 h-4 text-tbc-gold flex-shrink-0" />
                        <a
                          href={`mailto:${business.email}`}
                          onClick={() => trackEvent(business.business_id, "contact_click", { metadata: { channel: "email" } })}
                          className="text-gray-600 font-body text-sm hover:text-tbc-navy break-all"
                        >
                          {business.email}
                        </a>
                      </div>
                    )}
                    {business.website && (
                      <div className="flex items-center gap-2.5">
                        <Globe className="w-4 h-4 text-tbc-gold flex-shrink-0" />
                        <a
                          href={business.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent(business.business_id, "contact_click", { metadata: { channel: "website" } })}
                          className="text-gray-600 font-body text-sm hover:text-tbc-navy"
                        >
                          Visit Website
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {business.social_links && Object.values(business.social_links).some(Boolean) && (
                  <div className="bg-tbc-cream rounded-2xl p-5" data-testid="social-links-display">
                    <h3 className="font-heading text-lg text-tbc-navy mb-3">Follow on social</h3>
                    <div className="flex flex-wrap gap-2.5">
                      {SOCIAL_PLATFORMS.map(({ key, label, icon: Icon }) => {
                        const url = resolveSocialUrl(key, business.social_links?.[key]);
                        if (!url) return null;
                        return (
                          <a
                            key={key}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`${business.name} on ${label}`}
                            className="w-10 h-10 rounded-full bg-tbc-navy text-tbc-gold flex items-center justify-center hover:bg-tbc-gold hover:text-tbc-navy transition-colors"
                            data-testid={`social-link-${key}`}
                          >
                            <Icon className="w-4 h-4" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {business.hours && Object.keys(business.hours).length > 0 && (
                  <div className="bg-tbc-cream rounded-2xl p-5">
                    <h3 className="font-heading text-lg text-tbc-navy mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-tbc-gold" /> Hours
                    </h3>
                    <div className="space-y-1.5">
                      {Object.entries(business.hours).map(([day, hours]) => (
                        <div key={day} className="flex justify-between text-sm font-body">
                          <span className="text-gray-600 capitalize">{day}</span>
                          <span className="text-tbc-navy">{hours}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </aside>
            </div>
          )}

          {activeTab === "photos" && (
            <div role="tabpanel" data-testid="tab-panel-photos">
              <PhotosTabPanel
                business={business}
                canManage={!!user && (isAdmin || user.user_id === business.owner_id)}
                onSaved={(imgs) => setBusiness((prev) => ({ ...prev, gallery_images: imgs }))}
              />
            </div>
          )}

          {activeTab === "reviews" && (
            <div role="tabpanel" data-testid="tab-panel-reviews" className="space-y-6">
              <ReviewsSection
                business={business}
                currentUser={user}
                refreshKey={reviewsRefreshKey}
                onWriteReview={
                  user && user.user_id === business.owner_id
                    ? undefined
                    : () => setReviewDialogOpen(true)
                }
              />
              <BusinessExternalReviewsSection businessId={business.business_id} />
            </div>
          )}
        </div>
      </section>

      {/* Related-businesses rail — powered by Blinterest category matches */}
      <RelatedBusinessesRail businessId={business.business_id} />

      <Footer />

      {/* Admin-only edit dialog — invisible to non-admins because setEditOpen never fires */}
      {isAdmin && (
        <BusinessEditDialog
          business={business}
          open={editOpen}
          onOpenChange={setEditOpen}
          onSaved={(updated) => {
            if (updated) setBusiness(updated);
            setEditOpen(false);
          }}
        />
      )}

      {/* Admin-only assign-owner dialog */}
      {isAdmin && (
        <AssignOwnerDialog
          business={business}
          open={assignOwnerOpen}
          onOpenChange={setAssignOwnerOpen}
          onAssigned={(updated) => {
            if (updated) setBusiness((prev) => ({ ...prev, ...updated }));
          }}
        />
      )}

      {/* Admin-only Claim QR dialog */}
      {isAdmin && (
        <ClaimQRDialog
          business={business}
          open={claimQrOpen}
          onOpenChange={setClaimQrOpen}
        />
      )}
    </div>
  );
}
